import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  useTheme,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { KPILimit, useKPIStore } from '../../stores/kpi.store';
import { StatisticType } from '../../types/dashboard.type';
import { SetupCoachmark } from '../selfSetup/SetupCoachmark';
import { helpI18n } from '../../i18n/help';

interface KPILimitsDialogProps {
  open: boolean;
  onClose: () => void;
  /** Knowledge Base try-it step 2: spotlight the form. */
  helpGuide?: boolean;
  onHelpGuideDone?: () => void;
  /** Self-setup tour tip inside this dialog. */
  tourTip?: "kpiForm" | "kpiList" | null;
  onTourNext?: () => void;
  onTourDismiss?: () => void;
  tourFormTitle?: string;
  tourFormSubtitle?: string;
  tourListTitle?: string;
  tourListSubtitle?: string;
  tourNextLabel?: string;
}

const KPILimitsDialog: React.FC<KPILimitsDialogProps> = ({
  open,
  onClose,
  helpGuide = false,
  onHelpGuideDone,
  tourTip = null,
  onTourNext,
  onTourDismiss,
  tourFormTitle,
  tourFormSubtitle,
  tourListTitle,
  tourListSubtitle,
  tourNextLabel,
}) => {
  const theme = useTheme();
  const { t: tHelp } = useTranslation('translation', { i18n: helpI18n });
  const { limits, addLimit, removeLimit, toggleLimit } = useKPIStore();
  const [newLimit, setNewLimit] = useState<Partial<KPILimit>>({
    name: '',
    value: 0,
    operator: '>',
    alertMessage: '',
    isEnabled: true,
  });

  const selfSetupTour = Boolean(tourTip);
  const spotlight = helpGuide || selfSetupTour;

  const handleAddLimit = () => {
    if (newLimit.name && newLimit.alertMessage) {
      addLimit({
        id: Math.random().toString(36).substr(2, 9),
        name: newLimit.name as StatisticType,
        value: newLimit.value || 0,
        operator: newLimit.operator || '>',
        alertMessage: newLimit.alertMessage,
        isEnabled: true,
      });
      setNewLimit({
        name: '',
        value: 0,
        operator: '>',
        alertMessage: '',
        isEnabled: true,
      });
      if (helpGuide) {
        onHelpGuideDone?.();
      }
    }
  };

  const formBlock = (
    <Box id="self-setup-kpi-form" sx={{ mb: 4, width: '100%' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Add New Limit</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>KPI</InputLabel>
          <Select
            value={newLimit.name}
            label="KPI"
            onChange={(e) => setNewLimit({ ...newLimit, name: e.target.value })}
          >
            {Object.values(StatisticType).map((stat) => (
              <MenuItem key={stat} value={stat}>
                {stat.replace('_', ' ').toUpperCase()}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Operator</InputLabel>
          <Select
            value={newLimit.operator}
            label="Operator"
            onChange={(e) => setNewLimit({ ...newLimit, operator: e.target.value as KPILimit['operator'] })}
          >
            <MenuItem value=">">Greater Than</MenuItem>
            <MenuItem value="<">Less Than</MenuItem>
            <MenuItem value="=">Equal To</MenuItem>
            <MenuItem value=">=">Greater or Equal</MenuItem>
            <MenuItem value="<=">Less or Equal</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Value"
          type="number"
          value={newLimit.value}
          onChange={(e) => setNewLimit({ ...newLimit, value: Number(e.target.value) })}
          sx={{ minWidth: 120 }}
        />
        <TextField
          label="Alert Message"
          value={newLimit.alertMessage}
          onChange={(e) => setNewLimit({ ...newLimit, alertMessage: e.target.value })}
          sx={{ minWidth: 300 }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddLimit}
        >
          Add Limit
        </Button>
      </Box>
    </Box>
  );

  const listBlock = (
    <Box id="self-setup-kpi-list" sx={{ width: '100%' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Current Limits</Typography>
      <List>
        {limits.map((limit) => (
          <ListItem
            key={limit.id}
            sx={{
              bgcolor: theme.palette.background.default,
              mb: 1,
              borderRadius: '8px',
            }}
          >
            <ListItemText
              primary={
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {limit.name.replace('_', ' ').toUpperCase()}
                </Typography>
              }
              secondary={
                <Typography variant="body2" color="text.secondary">
                  Alert when {limit.name.replace('_', ' ')} {limit.operator} {limit.value}
                  {limit.alertMessage && ` - ${limit.alertMessage}`}
                </Typography>
              }
            />
            <ListItemSecondaryAction>
              <Switch
                edge="end"
                checked={limit.isEnabled}
                onChange={() => toggleLimit(limit.id)}
                sx={{ mr: 2 }}
              />
              <IconButton
                edge="end"
                onClick={() => removeLimit(limit.id)}
                sx={{ color: theme.palette.error.main }}
              >
                <DeleteIcon />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: theme.palette.background.paper,
          borderRadius: "16px",
          ...(spotlight && { overflow: "visible" }),
        }
      }}
    >
      <DialogTitle sx={{
        bgcolor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        borderRadius: "16px 16px 0 0",
        ...(spotlight && { position: "relative", zIndex: 0 }),
      }}>
        KPI Limits & Alerts
      </DialogTitle>
      <DialogContent
        sx={{
          p: 3,
          ...(spotlight && { overflow: "visible" }),
        }}
      >
        <SetupCoachmark
          open={helpGuide || tourTip === "kpiForm"}
          title={
            tourTip === "kpiForm"
              ? (tourFormTitle ?? "")
              : tHelp('knowledgeBase.kpiGuideFormTitle')
          }
          subtitle={
            tourTip === "kpiForm"
              ? (tourFormSubtitle ?? "")
              : tHelp('knowledgeBase.kpiGuideFormSubtitle')
          }
          onDismiss={() => {
            if (tourTip === "kpiForm") onTourDismiss?.();
            else onHelpGuideDone?.();
          }}
          onNext={() => {
            if (tourTip === "kpiForm") onTourNext?.();
            else onHelpGuideDone?.();
          }}
          nextLabel={
            tourTip === "kpiForm"
              ? (tourNextLabel ?? tHelp('knowledgeBase.kpiGuideFormDone'))
              : tHelp('knowledgeBase.kpiGuideFormDone')
          }
          fullWidth
        >
          {formBlock}
        </SetupCoachmark>

        <SetupCoachmark
          open={tourTip === "kpiList"}
          title={tourListTitle ?? ""}
          subtitle={tourListSubtitle ?? ""}
          onDismiss={() => onTourDismiss?.()}
          onNext={() => onTourNext?.()}
          nextLabel={tourNextLabel}
          fullWidth
        >
          {listBlock}
        </SetupCoachmark>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={onClose}
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': {
              color: theme.palette.text.primary,
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KPILimitsDialog;
